// src/components/auth/SignupForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { showToast } from '../../utils/helpers';
import api from "../../utils/api";
import { API_CONFIG } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Phone, 
  Mail, 
  Lock, 
  User, 
  Building2,
  ChevronLeft,
  Eye, 
  EyeOff, 
  CheckCircle2, 
  AlertCircle,
  ArrowRight,
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  Loader
} from 'lucide-react';

const SignupForm = ({ onLogin, userEmail }) => {
  
  const { currentUser, setUserData, refreshUserData } = useAuth();
  const [signupMethod, setSignupMethod] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [userId, setUserID] = useState(null);
  const [googleUserInfo, setGoogleUserInfo] = useState(null);
  const [requiresPhoneVerification, setRequiresPhoneVerification] = useState(false);  
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);
  const [formData, setFormData] = useState({
    countryCode: '+234',
    phonenumber: '',
    otp: ['', '', '', '', '', ''],
    fullname: '',
    company_name: '',
    company_email: userEmail || '',
    password: '',
    confirmPassword: '',
    rc_number: '',
    userId: '',
    company_id: '',
    business_certificate: null,
    ownsBusiness: false
  });
  const [showPassword, setShowPassword] = useState({
    createpass: false,
    confirmcreatepass: false
  });
  const [phoneError, setPhoneError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [businessNameError, setBusinessNameError] = useState('');
  const [businessNameStatus, setBusinessNameStatus] = useState('idle');
  const [isBusinessRegistered, setIsBusinessRegistered] = useState(false);
  const [googleAuthSuccess, setGoogleAuthSuccess] = useState(false);
  const [isCheckingBusiness, setIsCheckingBusiness] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpInputRefs = useRef([]);
  const businessCheckTimeout = useRef(null);
  const fileInputRef = useRef(null);

  // Resolve the user ID for OTP requests: prefers the ID captured during
  // signup, falls back to the logged-in session so the user ID is always
  // attached to verify-otp / complete-google-activation requests.
  const resolveUserId = () => userId || currentUser?.id || null;

  const countryCodes = [
    { code: '+234', country: 'NG', flag: '🇳🇬' },
    { code: '+233', country: 'GH', flag: '🇬🇭' },
    { code: '+254', country: 'KE', flag: '🇰🇪' },
    { code: '+27', country: 'ZA', flag: '🇿🇦' },
    { code: '+1', country: 'US', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' }
  ];

  useEffect(() => {
    if (userEmail) {
      setFormData(prev => ({
        ...prev,
        company_email: userEmail
      }));
    }
  }, [userEmail]);

  useEffect(() => {
    if (currentStep === 3 && googleUserInfo && !formData.company_name) {
      setFormData(prev => ({
        ...prev,
        company_name: googleUserInfo.name,
        fullname: googleUserInfo.name,
        company_email: googleUserInfo.email
      }));
    }
  }, [currentStep, googleUserInfo, formData.company_name]);

  useEffect(() => {
    otpInputRefs.current = otpInputRefs.current.slice(0, 6);
  }, []);

  useEffect(() => {
    return () => {
      if (businessCheckTimeout.current) {
        clearTimeout(businessCheckTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      if (name === 'company_name') {
        setBusinessNameError('');
        setBusinessNameStatus('idle');
        
        if (businessCheckTimeout.current) {
          clearTimeout(businessCheckTimeout.current);
        }
        
        if (value.trim().length > 2) {
          businessCheckTimeout.current = setTimeout(() => {
            checkBusinessNameAvailability(value.trim());
          }, 500);
        }
      }
    }

    if (name === 'phonenumber') setPhoneError('');
  };

  const checkBusinessNameAvailability = async (businessName) => {
    if (!businessName || businessName.length < 3) {
      setBusinessNameStatus('idle');
      return;
    }

    setIsCheckingBusiness(true);
    try {
  
   const sanitizedCountryCode = formData.countryCode ? formData.countryCode.replace(/^\+/, '') : '234';
  const sanitizedPhoneNumber = formData.phonenumber ? formData.phonenumber.replace(/^0+/, '') : '';
  const finalPhoneNumber = `${sanitizedCountryCode}${sanitizedPhoneNumber}`;
      const registeredResponse = await api.post(API_CONFIG.AUTH.IS_REGISTERED_BUSINESS, {
        businessName: businessName,
        userId: finalPhoneNumber || userId
      });
      const registeredData = await registeredResponse.data;

      if (registeredData.error) {
        setBusinessNameStatus('idle');
        setBusinessNameError(registeredData.message || 'Error checking business name');
        setIsBusinessRegistered(false);
      } else if (registeredData.isRegistered) {
        setBusinessNameStatus('registered');
        setBusinessNameError('This business name is registered with CAC. If you own this business, please check the box below.');
        setIsBusinessRegistered(true);
      } else {
        setBusinessNameStatus('available');
        setBusinessNameError('');
        setIsBusinessRegistered(false);
      }
    } catch (error) {
      console.error('Business check error:', error);
      const errMsg = error.response?.data?.message || error.message || 'Error checking business name. Please try again.';
      setBusinessNameStatus('idle');
      setBusinessNameError(errMsg);
    } finally {
      setIsCheckingBusiness(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB', 'error');
        return;
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showToast('Please upload a PDF, JPG, or PNG file', 'error');
        return;
      }

      setFormData(prev => ({
        ...prev,
        business_certificate: file
      }));

      if (file.type === 'application/pdf') {
        setFilePreview({
          type: 'pdf',
          name: file.name,
          size: file.size
        });
      } else if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview({
            type: 'image',
            url: e.target.result,
            name: file.name,
            size: file.size
          });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const removeFile = () => {
    setFormData(prev => ({
      ...prev,
      business_certificate: null
    }));
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...formData.otp];
    newOtp[index] = value;
    setFormData(prev => ({
      ...prev,
      otp: newOtp
    }));

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    setOtpError('');
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...formData.otp];
    for (let i = 0; i < pastedData.length; i++) {
      if (i < 6) {
        newOtp[i] = pastedData[i];
      }
    }
    setFormData(prev => ({
      ...prev,
      otp: newOtp
    }));

    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    otpInputRefs.current[lastFilledIndex]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Step 0: Choose Signup Method
  const handleSignupMethodSelect = (method) => {
 
    
    if (method === 'google') {
    setSignupMethod(method);

      setIsGoogleLoading(true);
      handleGoogleSignup();
    } else {
      // setIsPhoneLoading(true);
      window.location.href = `${process.env.REACT_APP_WHATSAPP_ENDPOINT}&text=sell_on_paymeo`;
      // Add a small delay to show the loader before navigating
      // setTimeout(() => {
      //   setIsPhoneLoading(false);
      //   setCurrentStep(1);
      // }, 500);
    }
  };

  // Google Signup Handler
  const handleGoogleSignup = async () => {
    try {
      if (!window.google || !window.google.accounts) {
        showToast('Google sign-in is not available. Please try another method.', 'error');
        setIsGoogleLoading(false);
        setSignupMethod(null);
        return;
      }

      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        callback: async (response) => {
          if (response.access_token) {
            try {
              const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v1/userinfo', {
                headers: {
                  Authorization: `Bearer ${response.access_token}`
                }
              });
              
              if (!userInfoResponse.ok) {
                throw new Error('Failed to fetch user info from Google');
              }
              
              const userInfo = await userInfoResponse.json();
              setGoogleUserInfo(userInfo);
              
              const backendResponse = await api.post(API_CONFIG.AUTH.GOOGLE_REGISTER, {
                email: userInfo.email,
                fullname: userInfo.name,
                idToken: response.id_token,
                accessToken: response.access_token
              });

              const data = await backendResponse.data;

              if (data.success) {
        // Prefer the DB row id (data.userId) — the activation endpoint and
        // OTP storage key off user_accounts.id, not the company_id.
        if (data.userId) {
          setUserID(data.userId);
        } else if (data.user_com_id) {
          setUserID(data.user_com_id);
        }
                setGoogleAuthSuccess(true);

                // WhatsApp number verification is ALWAYS required as part of
                // Google signup — the user must verify their number before
                // going anywhere else. We never skip straight to the dashboard
                // even when the account already has a phone on file (e.g. from
                // a partial or previous attempt), otherwise the user ends up
                // on the complete-profile page unverified.
                if (data.user && data.token) {
                  setUserData(data.user, data.account, data.token);
                }
                showToast('Google authentication successful! Please verify your phone number.', 'success');
                setRequiresPhoneVerification(true);
                setCurrentStep(1);
              } else {
                const errorMessage = data.message || data.error || 'Google signup failed';
                showToast(errorMessage, 'error');
                setSignupMethod(null);
                setCurrentStep(1);
              }
            } catch (error) {
              console.error('Google signup error:', error);
              showToast('Failed to complete Google signup', 'error');
              setSignupMethod(null);
              setCurrentStep(1);
            } finally {
              setIsGoogleLoading(false);
            }
          } else if (response.error) {
            console.error('Google OAuth error:', response.error);
            if (response.error !== 'popup_closed_by_user') {
              showToast('Google sign-in was cancelled or failed', 'error');
            }
            setSignupMethod(null);
            setCurrentStep(1);
            setIsGoogleLoading(false);
          }
        }
      });

      client.requestAccessToken();
    } catch (error) {
      console.error('Google signup initialization error:', error);
      showToast('Google signup failed to initialize', 'error');
      setSignupMethod(null);
      setCurrentStep(1);
      setIsGoogleLoading(false);
    }
  };

  // Step 1: Verify Phone Number
  const handleSendOTP = async () => {
    if (!formData.phonenumber.trim()) {
      setPhoneError('Phone number is required');
      return;
    }

    setIsVerifying(true);
    setPhoneError('');

    try {
      const payload = {
        countryCode: formData.countryCode,
        phoneNumber: formData.phonenumber,
        // Always attach the user ID so the backend can bind this OTP
        // request to the specific account.
        userId: resolveUserId()
      };

      let response = "";

      if (signupMethod === 'google') {
        response = await api.post(API_CONFIG.AUTH.VERIFY_PHONE, payload);
      }

      if (signupMethod === 'phone') {
        response = await api.post(API_CONFIG.AUTH.REGISTER_PHONE, payload);
      }

      const data = await response.data;

 
      if (data.error) {
        setPhoneError(data.message || data.error);
      } else {
        // register-phone / verify-phone return the account id as `userId`
        setUserID(data.userId || data.user_com_id || userId);
        showToast('OTP sent successfully', 'success');
        setCurrentStep(2);
      }
    } catch (error) {
      setPhoneError('Failed to send OTP. Please try again.');
      console.error('OTP send error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP for current verification
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setIsVerifying(true);
    setOtpError('');

    try {
      const payload = {
        countryCode: formData.countryCode,
        phoneNumber: formData.phonenumber,
        userId: resolveUserId()
      };

      if (signupMethod === 'google') {
        await api.post(API_CONFIG.AUTH.RESEND_VERIFICATION_OTP, payload);
      } else {
        await api.post(API_CONFIG.AUTH.REGISTER_PHONE, payload);
      }

      showToast('OTP resent successfully', 'success');
      setResendCooldown(60);
    } catch (error) {
      setOtpError('Failed to resend OTP. Please try again.');
      console.error('OTP resend error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    const otpString = formData.otp.join('');
    if (!otpString || otpString.length !== 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }

    setIsVerifying(true);
    setOtpError('');

    try {
      if (signupMethod === 'google') {
        // Complete Google activation: verifies the OTP, saves the WhatsApp
        // number on the account, creates the payment account, creates the
        // login session and returns a token (auto-saved by the api interceptor).
        const payload = {
          userId: resolveUserId(),
          countryCode: formData.countryCode,
          phoneNumber: formData.phonenumber,
          otp: otpString
        };

        const response = await api.post(API_CONFIG.AUTH.COMPLETE_GOOGLE_ACTIVATION, payload);
        const data = await response.data;

        if (data.error || !data.success) {
          setOtpError(data.message || data.error || 'Failed to verify OTP. Please try again.');
        } else {
          showToast('Phone number verified successfully', 'success');
          // Hydrate the full user session so the app boots straight into
          // the setup flow, then proceed to market selection.
          await refreshUserData();
          window.location.href = '/setup/select-market';
        }
      } else {
        const payload = {
          countryCode: formData.countryCode,
          phoneNumber: formData.phonenumber,
          otp: otpString,
          userId: resolveUserId()
        };

        const response = await api.post(API_CONFIG.AUTH.VERIFY_OTP, payload);
        const data = await response.data;

        if (data.error) {
          setOtpError(data.error);
        } else {
          showToast('Phone number verified successfully', 'success');
          setCurrentStep(3);
        }
      }
    } catch (error) {
      setOtpError('Failed to verify OTP. Please try again.');
      console.error('OTP verification error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  // Determine if continue button should be enabled
  const isContinueButtonEnabled = () => {
    if (!formData.company_name.trim()) return false;
    if (businessNameStatus === 'taken') return false;
    if (isBusinessRegistered && !formData.ownsBusiness) return false;
    if (isCheckingBusiness) return false;
    return true;
  };

  const handleStep3Continue = () => {
    if (!formData.company_name.trim()) {
      setBusinessNameError('Business name is required');
      return;
    }

    if (businessNameStatus === 'taken') {
      setBusinessNameError('Please choose a different business name');
      return;
    }

    if (isBusinessRegistered && !formData.ownsBusiness) {
      setBusinessNameError('Please confirm business ownership to continue');
      return;
    }

    if (isBusinessRegistered && formData.ownsBusiness) {
      setCurrentStep(4);
    } else {
      if (signupMethod === 'google') {
        handleGoogleFinalSubmit();
      } else {
        setCurrentStep(5);
      }
    }
  };

  // Business Verification Step
  const handleBusinessVerification = async () => {
    if (!formData.rc_number.trim() || !formData.business_certificate) {
      showToast('RC number and business certificate are required', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('rc_number', formData.rc_number);
      formDataToSend.append('business_certificate', formData.business_certificate);
      formDataToSend.append('businessName', formData.company_name);
      formDataToSend.append('userId', userId);

      const response = await api.post(API_CONFIG.AUTH.VERIFY_BUSINESS, formDataToSend);
      const data = await response.data;

      if (data.error) {
        showToast(data.error, 'error');
      } else {
        showToast('Business verification submitted successfully', 'success');
        if (signupMethod === 'google') {
          handleGoogleFinalSubmit();
        } else {
          setCurrentStep(5);
        }
      }
    } catch (error) {
      showToast('Business verification failed. Please try again.', 'error');
      console.error('Business verification error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Final submission for Google users
  const handleGoogleFinalSubmit = async () => {
    setIsLoading(true);
    try {
         const sanitizedCountryCode = formData.countryCode ? formData.countryCode.replace(/^\+/, '') : '234';
  const sanitizedPhoneNumber = formData.phonenumber ? formData.phonenumber.replace(/^0+/, '') : '';
  const finalPhoneNumber = `${sanitizedCountryCode}${sanitizedPhoneNumber}`;

      const formDataToSend = new FormData();
      

      formDataToSend.append('company_name', formData.company_name);
      formDataToSend.append('fullname', formData.fullname);
      formDataToSend.append('email', formData.company_email);
      formDataToSend.append('signupMethod', 'google');
      formDataToSend.append('phoneNumber', finalPhoneNumber);
      // formDataToSend.append('company_id', formData.company_id );

 

      if (isBusinessRegistered && formData.ownsBusiness) {
        formDataToSend.append('rc_number', formData.rc_number);
        if (formData.business_certificate) {
          formDataToSend.append('business_certificate', formData.business_certificate);
        }
      }

      const response = await api.post(API_CONFIG.AUTH.COMPLETE_GOOGLE_SIGNUP, formDataToSend);
      const data = await response.data;

      if (data.success || data.status == "success") {
        if (data.user && data.token) {
          setUserData(data.user, data.account, data.token);
          showToast('Account created successfully! Welcome!', 'success');
          setTimeout(() => {
            window.location.href = '/setup/select-market';
          }, 500);
        } else {
          showToast('Account created successfully!', 'success');
          window.location.href = '/setup/select-market';
        }
      } else {
        showToast(data.message || 'Failed to create account', 'error');
      }
    } catch (error) {
      showToast('Something went wrong. Please try again.', 'error');
      console.error('Google signup completion error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFinalStep()) return;

    setIsLoading(true);
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== 'confirmPassword' && key !== 'otp' && value !== null) {
          formDataToSend.append(key, value);
        }
      });

      formDataToSend.append('otp', formData.otp.join(''));
      formDataToSend.append('signupMethod', signupMethod);

      const response = await api.post(API_CONFIG.AUTH.SIGNUP, formDataToSend);
      const data = await response.data;

      if (data.error) {
        showToast(data.error, 'error');
      } else {
        showToast('Account created successfully!', 'success');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      showToast('Something went wrong. Please try again.', 'error');
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateFinalStep = () => {
    if (!formData.company_email.trim()) {
      showToast('Email is required', 'error');
      return false;
    }
    if (!formData.password.trim()) {
      showToast('Password is required', 'error');
      return false;
    }
    if (formData.password.length < 8) {
      showToast('Password must be at least 8 characters long', 'error');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      showToast('Passwords do not match', 'error');
      return false;
    }
    return true;
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else if (signupMethod) {
      setSignupMethod(null);
      setRequiresPhoneVerification(false);
      setGoogleAuthSuccess(false);
      setGoogleUserInfo(null);
      setIsGoogleLoading(false);
      setIsPhoneLoading(false);
    }
  };

  // Render Methods
  const renderMethodSelection = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="mb-8 text-center">
        <div className="flex justify-center mb-4 sm:mb-5">
          <img
            src="/assets/favicon.png"
            alt="Paymeo Logo"
            className="w-[clamp(40px,8vw,64px)] h-[clamp(40px,8vw,64px)]"
          />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2">Create your business account.</h2>
        <p className="text-gray-500">Choose how you'd like to sign up</p>
      </div>

   {/* Phone Signup Button */}
<button
  type="button"
  className="w-full flex items-center justify-center px-4 py-5 rounded-full bg-blue-600 hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
  onClick={() => handleSignupMethodSelect('phone')}
  disabled={isPhoneLoading}
>
  {isPhoneLoading ? (
    <div className="flex items-center justify-center gap-2">
      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      <span className="font-bold text-[15px] text-white">Processing...</span>
    </div>
  ) : (
    <span className="font-bold text-[15px] text-white">Continue with WhatsApp</span>
  )}
</button>

{/* Google Signup Button */}
<div className="flex justify-center">
  <button
  type="button"
  className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-gray-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto sm:min-w-[400px] max-w-full"
  onClick={() => handleSignupMethodSelect('google')}
  disabled={isGoogleLoading}
>
    <div className="w-5 h-5 flex items-center justify-center">
      {!isGoogleLoading && (
        <img 
          src="https://www.google.com/favicon.ico" 
          alt="Google" 
          className="w-5 h-5"
        />
      )}
    </div>
    
    {isGoogleLoading ? (
      <div className="flex items-center justify-center gap-2">
        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="font-bold text-[14px] text-gray-900 dark:text-white">Processing...</span>
      </div>
    ) : (
      <span className="font-bold text-[14px] text-gray-900 dark:text-white">Continue with Google</span>
    )}
  </button>
</div>

      {/* Divider with "Or" - Matching personal UI */}
      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">Or</span>
        </div>
      </div>

      {/* Sign In Link - Matching personal UI style */}
      <button
        type="button"
        onClick={onLogin}
        className="w-full py-4 text-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-full transition-all font-medium"
      >
        Sign in
      </button>
    </div>
  );

  const renderPhoneStep = () => (
    <div className="space-y-6 animate-fade-in">
      <button
        type="button"
        onClick={prevStep}
        className="p-2 -ml-2 text-gray-500 hover:text-gray-900 transition-colors"
      >
        <ChevronLeft size={24} />
      </button>

      <div className="mb-6">
        <h2 className="text-3xl font-black text-gray-900 mb-2">Verify your phone</h2>
        <p className="text-gray-500">Step 1 of {signupMethod === 'google' ? '2' : '5'}: WhatsApp phone verification</p>
      </div>

      {signupMethod === 'google' && googleAuthSuccess && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl mb-4">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 size={18} className="text-green-600" />
          </div>
          <div>
            <p className="font-bold text-green-800">Google Account Connected</p>
            <p className="text-sm text-green-600">{googleUserInfo?.email}</p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">Phone Number</label>
          <div className="flex gap-2">
            <div className="relative w-28">
              <select
                name="countryCode"
                className="w-full px-3 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none appearance-none text-gray-900"
                value={formData.countryCode}
                onChange={handleInputChange}
              >
                {countryCodes.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                <ChevronLeft size={16} className="rotate-90" />
              </div>
            </div>
            
            <div className="flex-1 relative group">
              <div className="absolute inset-y-0 left-4 flex items-center text-gray-400 group-focus-within:text-blue-500 transition-colors">
                <Phone size={18} />
              </div>
              <input
                type="tel"
                name="phonenumber"
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all"
                value={formData.phonenumber}
                onChange={handleInputChange}
                required
                placeholder="Phone number"
              />
            </div>
          </div>
          {phoneError && (
            <div className="flex items-center gap-2 text-red-500 text-sm mt-2 px-1">
              <AlertCircle size={14} />
              <span>{phoneError}</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className="w-full py-4 text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-lg shadow-blue-600/20 mt-4 disabled:opacity-50 transition-all"
          onClick={handleSendOTP}
          disabled={isVerifying}
        >
          {isVerifying ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Sending...
            </div>
          ) : (
            'Send Verification Code'
          )}
        </button>
      </div>
    </div>
  );

  const renderOTPStep = () => (
    <div className="space-y-6 animate-fade-in">
      <button
        type="button"
        onClick={prevStep}
        className="p-2 -ml-2 text-gray-500 hover:text-gray-900 transition-colors"
      >
        <ChevronLeft size={24} />
      </button>

      <div className="mb-6">
        <h2 className="text-3xl font-black text-gray-900 mb-2">Enter verification code</h2>
        <p className="text-gray-500">Step 2 of {signupMethod === 'google' ? '2' : '5'}: Code sent to {formData.phonenumber}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <label className="text-sm font-bold text-gray-700">6-digit code</label>
            <button
              type="button"
              className={`text-xs font-bold ${resendCooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 hover:underline'}`}
              onClick={handleResendOTP}
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend?'}
            </button>
          </div>
          <div className="flex justify-center gap-2">
            {formData.otp.map((digit, index) => (
              <input
                key={index}
                ref={el => otpInputRefs.current[index] = el}
                type="text"
                maxLength="1"
                className="w-14 h-14 text-center bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-2xl font-bold text-gray-900"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={handleOtpPaste}
              />
            ))}
          </div>
          {otpError && (
            <div className="flex items-center justify-center gap-2 text-red-500 text-sm mt-2">
              <AlertCircle size={14} />
              <span>{otpError}</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className="w-full py-4 text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-lg shadow-blue-600/20 mt-8 disabled:opacity-50 transition-all"
          onClick={handleVerifyOTP}
          disabled={isVerifying}
        >
          {isVerifying ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Verify Code'
          )}
        </button>
      </div>
    </div>
  );

  const renderProfileStep = () => (
    <div className="space-y-6 animate-fade-in max-h-[70vh] overflow-y-auto px-1 custom-scrollbar">
      <button
        type="button"
        onClick={prevStep}
        className="p-2 -ml-2 text-gray-500 hover:text-gray-900 transition-colors sticky top-0 bg-white z-10"
      >
        <ChevronLeft size={24} />
      </button>

      <div className="mb-6">
        <h2 className="text-3xl font-black text-gray-900 mb-2">Business profile</h2>
        <p className="text-gray-500">Step 3 of {signupMethod === 'google' ? '4' : '5'}: Tell us about your business</p>
      </div>

      <div className="space-y-4">
        {signupMethod === 'phone' && (
          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-700 ml-1">Full Name</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-4 flex items-center text-gray-400 group-focus-within:text-blue-500 transition-colors">
                <User size={18} />
              </div>
              <input
                type="text"
                name="fullname"
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all"
                value={formData.fullname}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
              />
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">Business Name</label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-4 flex items-center text-gray-400 group-focus-within:text-blue-500 transition-colors">
              <Building2 size={18} />
            </div>
            <input
              type="text"
              name="company_name"
              className={`w-full pl-12 pr-12 py-4 bg-gray-50 border rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all ${
                businessNameStatus === 'available' ? 'border-green-300 bg-green-50' :
                businessNameStatus === 'registered' ? 'border-yellow-300 bg-yellow-50' :
                'border-gray-200'
              }`}
              value={formData.company_name}
              onChange={handleInputChange}
              required
              placeholder="Enter your business name"
            />
            {isCheckingBusiness && (
              <div className="absolute right-4 top-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              </div>
            )}
            {businessNameStatus === 'available' && !isCheckingBusiness && (
              <div className="absolute right-4 top-4">
                <CheckCircle2 size={20} className="text-green-500" />
              </div>
            )}
          </div>
          
          {businessNameStatus === 'checking' && (
            <p className="text-blue-600 text-sm mt-2 flex items-center">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
              Checking business name availability...
            </p>
          )}
          {businessNameStatus === 'available' && (
            <p className="text-green-600 text-sm mt-2 flex items-center">
              <CheckCircle2 size={14} className="mr-1" />
              Business name is available
            </p>
          )}
          {businessNameStatus === 'registered' && (
            <p className="text-yellow-600 text-sm mt-2">{businessNameError}</p>
          )}
          
          {isBusinessRegistered && (
            <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-2xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="ownsBusiness"
                  checked={formData.ownsBusiness}
                  onChange={handleInputChange}
                  className="w-4 h-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-yellow-800">
                  I confirm that I am the legal owner of this registered business
                </span>
              </label>
            </div>
          )}
        </div>

        <button
          type="button"
          className="w-full py-4 text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-lg shadow-blue-600/20 mt-4 disabled:opacity-50 transition-all"
          onClick={handleStep3Continue}
          disabled={!isContinueButtonEnabled()}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderBusinessVerificationStep = () => (
    <div className="space-y-6 animate-fade-in">
      <button
        type="button"
        onClick={prevStep}
        className="p-2 -ml-2 text-gray-500 hover:text-gray-900 transition-colors"
      >
        <ChevronLeft size={24} />
      </button>

      <div className="mb-6">
        <h2 className="text-3xl font-black text-gray-900 mb-2">Business verification</h2>
        <p className="text-gray-500">Step 4 of {signupMethod === 'google' ? '4' : '5'}: Verify business ownership</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-yellow-800 text-sm">
            Your business appears to be registered with CAC. Please verify ownership to continue.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">RC Number</label>
          <input
            type="text"
            name="rc_number"
            className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all"
            value={formData.rc_number}
            onChange={handleInputChange}
            required
            placeholder="Enter your RC number"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">Business Certificate</label>
          
          {!filePreview ? (
            <div 
              className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-400 transition-colors bg-gray-50"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium mb-1">Click to upload business certificate</p>
              <p className="text-gray-500 text-xs">PDF, JPG, PNG (Max 10MB)</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-gray-700">Uploaded File</span>
                <button
                  type="button"
                  onClick={removeFile}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>
              
              {filePreview.type === 'pdf' && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <FileText size={20} className="text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{filePreview.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(filePreview.size)}</p>
                  </div>
                </div>
              )}
              
              {filePreview.type === 'image' && (
                <div className="space-y-2">
                  <div className="relative bg-gray-50 rounded-xl overflow-hidden">
                    <img 
                      src={filePreview.url} 
                      alt="Preview" 
                      className="w-full h-32 object-contain"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 truncate flex-1 mr-2">{filePreview.name}</span>
                    <span className="text-xs text-gray-500">{formatFileSize(filePreview.size)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            name="business_certificate"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChange}
            className="hidden"
          />
          
          <p className="text-gray-500 text-xs mt-2">
            Accepted formats: PDF, JPG, PNG (Maximum file size: 10MB)
          </p>
        </div>

        <button
          type="button"
          className="w-full py-4 text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-lg shadow-blue-600/20 mt-8 disabled:opacity-50 transition-all"
          onClick={handleBusinessVerification}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Verify Business'
          )}
        </button>
      </div>
    </div>
  );

  const renderPasswordStep = () => (
    <div className="space-y-6 animate-fade-in max-h-[70vh] overflow-y-auto px-1 custom-scrollbar">
      <button
        type="button"
        onClick={prevStep}
        className="p-2 -ml-2 text-gray-500 hover:text-gray-900 transition-colors sticky top-0 bg-white z-10"
      >
        <ChevronLeft size={24} />
      </button>

      <div className="mb-6">
        <h2 className="text-3xl font-black text-gray-900 mb-2">Secure your account</h2>
        <p className="text-gray-500">Step 5 of 5: Create login credentials</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">Email Address</label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-4 flex items-center text-gray-400 group-focus-within:text-blue-500 transition-colors">
              <Mail size={18} />
            </div>
            <input
              type="email"
              name="company_email"
              className={`w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all ${
                userEmail ? 'bg-gray-100' : ''
              }`}
              value={formData.company_email}
              onChange={handleInputChange}
              readOnly={!!userEmail}
              required
              placeholder="business@example.com"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">Password</label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-4 flex items-center text-gray-400 group-focus-within:text-blue-500 transition-colors">
              <Lock size={18} />
            </div>
            <input
              type={showPassword.createpass ? 'text' : 'password'}
              name="password"
              className="w-full pl-12 pr-12 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all"
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder="Create a password (min. 8 characters)"
              minLength={8}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => togglePasswordVisibility('createpass')}
            >
              {showPassword.createpass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">Confirm Password</label>
          <div className="relative group">
            <input
              type={showPassword.confirmcreatepass ? 'text' : 'password'}
              className={`w-full px-4 py-4 bg-gray-50 border rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all pr-12 ${
                formData.confirmPassword && formData.password !== formData.confirmPassword 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-200'
              }`}
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                confirmPassword: e.target.value
              }))}
              required
              placeholder="Confirm your password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => togglePasswordVisibility('confirmcreatepass')}
            >
              {showPassword.confirmcreatepass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle size={14} />
              Passwords do not match
            </p>
          )}
        </div>

        <button
          type="submit"
          className="w-full py-4 text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-lg shadow-blue-600/20 mt-8 disabled:opacity-50 transition-all"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Creating Account...
            </div>
          ) : (
            'Create Account'
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full mt-3">
      {!signupMethod && renderMethodSelection()}
       { (isGoogleLoading || isPhoneLoading) && (
            <div className="flex flex-col items-center justify-center space-y-4 py-10">
              <Loader className="w-10 h-10 text-blue-600 animate-spin" />
              <p className="text-gray-600">Processing ...</p>
            </div>
          )}
      {signupMethod && (
        <>
          {currentStep === 1 && requiresPhoneVerification && renderPhoneStep()}
          {currentStep === 2 && requiresPhoneVerification && renderOTPStep()}
          {currentStep === 3 && renderProfileStep()}
          {currentStep === 4 && isBusinessRegistered && formData.ownsBusiness && renderBusinessVerificationStep()}
          {currentStep === 5 && signupMethod === 'phone' && renderPasswordStep()}
         
        </>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SignupForm;